type FlightRecordType = 'manual' | 'wayline' | 'command' | 'mixed' | 'unknown'
type FlightRecordStatus = 'flying' | 'completed' | 'interrupted'
type FlightRecordQuery = {
  page: number
  page_size: number
  aircraft_sn?: string
  flight_type?: FlightRecordType
  status?: FlightRecordStatus
  from?: number
  to?: number
}

const FLIGHT_TYPE_TEXT: Record<FlightRecordType, string> = {
  manual: '手动飞行',
  wayline: '航线飞行',
  command: '指令飞行',
  mixed: '混合飞行',
  unknown: '未知',
}

const FLIGHT_STATUS_TEXT: Record<FlightRecordStatus, string> = {
  flying: '飞行中',
  completed: '已完成',
  interrupted: '异常结束',
}

const DATA_SOURCE_TEXT: Record<string, string> = {
  dock_osd: '机场 OSD',
  pilot_osd: 'Pilot OSD',
  drc_osd: '虚拟座舱 OSD',
  legacy_wayline_job: '历史任务',
}

const DJI_WAYLINE_NAME_FORBIDDEN_PATTERN = /[<>:"/|?*._\\]/g

export function sanitizeWaylineName (name: string): string {
  return name.replace(DJI_WAYLINE_NAME_FORBIDDEN_PATTERN, '').trim().replace(/\s+/g, ' ')
}

export function flightTypeText (type: FlightRecordType): string {
  return FLIGHT_TYPE_TEXT[type] || FLIGHT_TYPE_TEXT.unknown
}

export function flightStatusText (status: FlightRecordStatus): string {
  return FLIGHT_STATUS_TEXT[status] || status
}

export function flightStatusColor (status: FlightRecordStatus): string {
  if (status === 'flying') return 'processing'
  if (status === 'completed') return 'success'
  return 'error'
}

export function formatFlightTime (timestamp?: number): string {
  if (!timestamp) return '--'
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return '--'
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
}

export function formatFlightDuration (seconds?: number): string {
  if (seconds === undefined || seconds === null || seconds < 0) return '--'
  const total = Math.floor(seconds)
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const remain = total % 60
  return [hours, minutes, remain].map(value => String(value).padStart(2, '0')).join(':')
}

export function formatFlightDistance (meters?: number): string {
  if (meters === undefined || meters === null) return '--'
  if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`
  return `${meters.toFixed(1)} m`
}

export function formatFlightHeight (meters?: number): string {
  if (meters === undefined || meters === null) return '--'
  return `${meters.toFixed(1)} m`
}

export function flightDataSourceText (sources?: string): string {
  if (!sources) return '--'
  return sources.split(',').filter(Boolean).map(source => DATA_SOURCE_TEXT[source] || source).join('、')
}

export function compactFlightRecordQuery (query: FlightRecordQuery): FlightRecordQuery {
  const compact: Record<string, any> = { ...query }
  Object.keys(compact).forEach(key => {
    const value = compact[key]
    if (value === undefined || value === null || value === '') {
      delete compact[key]
    }
  })
  return compact as FlightRecordQuery
}
